<script>
import NtvErrorMessage from '../_common/ErrorMessage';
import NtvLabel from '../_common/Label';
import NtvText from '../Text';

/**
 * The low-level input element that handles keyboard inputs. Handles labels and
 * error messages, but no validation. That should be from a higher order
 * component.
 */
export default {
  name: 'NtvInput',

  components: {
    NtvErrorMessage,
    NtvLabel,
    NtvText
  },

  props: {
    /**
     * The name of the input field. Mainly used for labeling
     * inputs for form submissions.
     */
    name: {
      type: String,
      default: null
    },
    /**
     * Label that is shown on top of the input
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Value of the input
     */
    value: {
      type: [String, Number, Boolean],
      default: ''
    },
    /**
     * Placeholder label if no value set.
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Width of the input.
     */
    width: {
      type: String,
      default: '100%'
    },
    /**
     * The tab index number to place on the input. (For accessibility)
     */
    tabIndex: {
      type: Number,
      default: 0
    },
    /**
     * Error message to be displayed under input. It will automatically be
     * bordered red.
     */
    errorMessage: {
      type: String,
      default: ''
    },
    /**
     * The message displayed in the info bubble.
     */
    infoMessage: {
      type: String,
      default: ''
    },
    /**
     * If value is set to anything but null, it will convert the input into
     * a textarea element. The number of will determine how many lines to set
     * within the textarea.
     */
    rows: {
      type: Number,
      default: null
    },
    /**
     * Place an astericks on the label to indicate it is required.
     */
    isRequired: {
      type: Boolean,
      default: false
    },
    /**
     * Disable all user inputs and grey it out.
     */
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Borderless with no user inputs allowed.
     */
    isReadOnly: {
      type: Boolean,
      default: false
    },
    /**
     * This is only to show Character Count below the input box. For restriction use input characterLimit attribute
     */
    characterLimit: {
      type: Number,
      default: null
    },
    /**
     * Remove the border from the Input
     * @type {Boolean}
     */
    isRemoveBorder: {
      type: Boolean,
      default: false
    },
    /**
     * By default, the textarea will grow longer based on the text entered.
     * Disable this to keep it within a certain number of rows.
     */
    disableAutoGrow: {
      type: Boolean,
      default: false
    },
    /**
     * The max height a text area can autogrow to (pixels only).
     */
    maxHeight: {
      type: String,
      default: ''
    },
    /**
     * Prevent the error message from wrapping within the width of the input
     */
    preventWrap: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    if (this.value) {
      // Wait for initial rendering of textarea first before applying height.
      this.$nextTick(() => {
        this.updateHeight();
      });
    }
  },

  data: function () {
    return {
      inputVal: this.value,
      height: 'auto',
      dIsCharOverLimit: 0,
      dIsCharOverLimitColor: 'grey'
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.updateHeight();
        }

        this.inputVal = newVal;

        this.checkIfCharIsOverLimit();

        // For some reason, updated value change from prop doesn't seem to
        // update the input's dom value so make sure this happens.
        if (this.$refs && this.$refs.input) {
          this.$refs.input.value = newVal;
        }
      }
    }
  },

  methods: {
    checkIfCharIsOverLimit() {
      this.dIsCharOverLimitColor = 'grey';
      this.dIsCharOverLimit = 0;
      if (this.characterLimit && this.inputVal) {
        this.dIsCharOverLimit = this.inputVal.length || 0;
        this.dIsCharOverLimitColor =
          this.inputVal.length > this.characterLimit ? 'danger' : 'grey';
      }
    },
    updateHeight() {
      if (this.rows && this.rows > 1) {
        if (this.$refs.input) {
          const scrollHeight = this.$refs.input.scrollHeight;

          // When used in a tab that is initially hidden, the initial scroll
          // height on the mounted dom node is 0 for some reason. So to cater for
          // this if the scroll height is less 10, don't apply the auto-grow
          // function and keep default height of auto.
          if (scrollHeight > 10) {
            // Need to add 2 pixels for the border heights.
            const maxHeight = this.maxHeight.replace('px', '');
            const scrollHeight = this.$refs.input.scrollHeight + 2;

            if (scrollHeight <= maxHeight) this.height = scrollHeight + 'px';
          }
        }
      }
    },
    onInput: function (evt) {
      this.inputVal = evt.currentTarget.value;

      this.updateHeight();

      this.checkIfCharIsOverLimit();

      /**
       * Input event handler.
       * Parameters: (val: String, evt: Event)
       * @event input
       */
      this.$emit('input', this.inputVal, evt);
    },
    onKeydown: function (evt) {
      /**
       * Keydown event handler.
       * Parameters: (evt: Event)
       * @event keydown
       */
      this.$emit('keydown', evt);
    },
    onChange: function (evt) {
      this.inputVal = evt.currentTarget.value;

      this.checkIfCharIsOverLimit();

      /**
       * Change event handler.
       * Parameters: (val: String, evt: Event)
       * @event change
       */
      this.$emit('change', this.inputVal, evt);
    },
    clearValue: function () {
      this.inputVal = '';
      this.$refs.input.value = '';
      this.$refs.input.dispatchEvent(new Event('change'));
      this.$refs.input.dispatchEvent(new Event('input'));
    },
    onBlur(evt) {
      if (this.isDisabled || this.isReadOnly) {
        return;
      }

      /**
       * Blur event handler. Parameters: (val: String, evt: Event)
       * @event blur
       */
      this.$emit('blur', evt.currentTarget.value, evt);
    },
    onFocus(evt) {
      if (this.isDisabled || this.isReadOnly) {
        return;
      }

      /**
       * Focus event handler. Parameters: (val: String, evt: Event)
       * @event focus
       */
      this.$emit('focus', evt.currentTarget.value, evt);
    },
    onPaste(evt) {
      if (this.isDisabled || this.isReadOnly) {
        return;
      }

      //Prevent Change/Input events to be fired after paste event.

      const value = (evt.clipboardData || window.clipboardData).getData('text');

      this.checkIfCharIsOverLimit();

      /**
       * Paste event handler. Parameters: (val: String, evt: Event)
       * @event paste
       */

      this.$emit('paste', value, evt);
    }
  }
};
</script>

<template>
  <div
    class="ntv-input"
    :style="{width: width}"
    :data-po="label"
  >
    <!-- Input Label -->
    <ntv-label
      class="mb-md"
      :label="label"
      :is-required="isRequired"
      :info-message="infoMessage"
    />

    <!-- Input/Textarea -->
    <div class="ntv-input__wrapper">
      <input
        :name="name"
        v-bind="$attrs"
        :class="[
          'ntv-input__input rounded border text-default text-base ntv-border--color', 
          {'ntv-input--error border-danger': errorMessage},
          {'border-grey-5 bg-grey-1 cursor-not-allowed text-grey': isDisabled},
          {'border-rock-blue bg-white focus:shadow-input': !isDisabled && !isReadOnly},
          {'border-transparent': isReadOnly},
          {'ntv-input__borderless': isRemoveBorder},
        ]"
        :placeholder="placeholder"
        :readonly="isReadOnly"
        :disabled="isDisabled"
        :value="inputVal"
        :tab-index="tabIndex"
        :required="isRequired"
        ref="input"
        @change="onChange"
        @input="onInput"
        @keydown="onKeydown"
        @blur="onBlur"
        @focus="onFocus"
        @paste="onPaste"
        v-if="!rows"
      >
      <textarea
        :name="name"
        v-bind="$attrs"
        :class="[
          'ntv-input__textarea rounded border text-default text-base ntv-border--color', 
          {'ntv-input--error border-danger': errorMessage},
          {'border-grey-5 bg-grey-1 cursor-not-allowed text-grey': isDisabled},
          {'border-rock-blue bg-white focus:shadow-input': !isDisabled && !isReadOnly},
          {'border-transparent': isReadOnly},
          {'ntv-input__borderless': isRemoveBorder},
        ]"
        :style="{
          maxHeight,
          minHeight: height,
          height
        }"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :readonly="isReadOnly"
        :value="inputVal"
        :tab-index="tabIndex"
        :required="isRequired"
        ref="input"
        @change="onChange"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
        @paste="onPaste"
        :rows="rows"
        v-else
      />
      <slot />
    </div>

    <div class="ntv-input--info flex items-center">
      <!-- Input Error Message -->
      <slot name="error-message">
        <ntv-error-message
          class="pt-md"
          :message="errorMessage"
          :prevent-wrap="preventWrap"
          v-if="errorMessage"
        />
      </slot>

      <div class="flex-1">
        <slot name="misc" />
      </div>

      <!-- Input Character Count -->
      <slot name="character-count">
        <ntv-text
          v-if="characterLimit"
          tag="div"
          :text="`${dIsCharOverLimit}/${this.characterLimit}`"
          class="pl-sm pt-md"
          :color="dIsCharOverLimitColor"
          size="default"
        />
      </slot>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../theme/theme.scss';

.ntv-input {
  display: block;
  line-height: 1;
  position: relative;
}

.ntv-input__borderless {
  border: none;
  outline: none;
}

.ntv-input__wrapper {
  position: relative;
  width: 100%;
}

.ntv-input__input,
.ntv-input__textarea {
  width: 100%;
  height: 34px;
  padding: 9px 15px;
  outline: 0;

  &::placeholder {
    color: $--grey;
    opacity: 1;
  }

  &[readonly] {
    border-radius: 0;
    border-bottom: 1px solid $--grey;
  }
}

.ntv-input__textarea {
  height: auto;
  line-height: 1.5;
  min-height: 40px !important;
}
</style>
